"use client";

import { useRef, useState } from "react";
import { afterNextPaint } from '../../lib/afterNextPaint';
import { DEFAULT_LOCALE, getSiteCopy, normalizeLocale } from '@/lib/siteI18n';
import { appendMetaAttributionParams } from './metaAttribution';
import { appendRedditAttributionParams } from './redditAttribution';
import { appendPromoteKitAttributionParams } from './promotekitAttribution';

// Inline SVG keeps precise Google colors without pulling in an icon lib weight
const GoogleIcon = (props) => (
  <svg width="18" height="18" viewBox="0 0 18 18" {...props} aria-hidden="true">
    <path fill="#EA4335" d="M9 7.3v3.6h5.1a4.7 4.7 0 0 1-2 3.1l3.2 2.5C17 14.9 18 12.3 18 9.6c0-.6-.1-1.3-.2-1.9H9z"/>
    <path fill="#34A853" d="M3.9 10.7 3.3 13l-3 .1A9 9 0 0 1 0 9.6a9 9 0 0 1 .3-2.4l3 .1 1.2 2.7z"/>
    <path fill="#4A90E2" d="M9 3.6c1.3 0 2.5.5 3.3 1.3l2.5-2.5A8.9 8.9 0 0 0 9 0a9 9 0 0 0-8 4.9l3.2 2.5A5.4 5.4 0 0 1 9 3.6z"/>
    <path fill="#FBBC05" d="M9 16.8c2.4 0 4.5-.8 6-2.3l-3.2-2.5c-.9.6-2 1-2.8 1a5.4 5.4 0 0 1-5-3.5L.8 14A9 9 0 0 0 9 16.8z"/>
  </svg>
);

const oneClickLabels = {
  en: '1-click',
  de: '1-Klick',
  fr: '1 clic',
};

const getClickLocation = (element) => {
  if (element?.closest?.('header')) return 'header';
  if (element?.closest?.('footer')) return 'footer';
  if (element?.closest?.('#free-audio-tools')) return 'free_audio_tools';
  if (element?.closest?.('#pricing')) return 'pricing';
  if (element?.closest?.('#hero-cta')) return 'hero';
  return 'body';
};

const dispatchTrack = (event, params = {}) => {
  try {
    window.dispatchEvent(new CustomEvent("track", { detail: { event, params }}));
  } catch {}
};

const getAppRedirectPath = (redirectPath) => {
  const sourceUrl = new URL(redirectPath, window.location.origin);
  if (sourceUrl.origin !== window.location.origin) return '/dashboard';
  const path = sourceUrl.pathname.replace(/^\/app(?=\/|$)/, '') || '/dashboard';
  return `${path}${sourceUrl.search}${sourceUrl.hash}`;
};

const buildAppSignupUrl = (redirectPath, trackingParams) => {
  const redirectUrl = new URL("/app/signup", window.location.origin);
  redirectUrl.searchParams.set('auto_oauth', 'google');
  redirectUrl.searchParams.set('redirect_path', getAppRedirectPath(redirectPath));
  redirectUrl.searchParams.set('from', 'landing');
  redirectUrl.searchParams.set('landing_path', trackingParams.landing_path);
  redirectUrl.searchParams.set('cta_location', trackingParams.cta_location || trackingParams.click_location);
  redirectUrl.searchParams.set('auth_target', trackingParams.auth_target);
  redirectUrl.searchParams.set('auth_method', trackingParams.auth_method);
  if (trackingParams.cta_id) redirectUrl.searchParams.set('cta_id', trackingParams.cta_id);
  if (trackingParams.plan_intent) redirectUrl.searchParams.set('plan_intent', trackingParams.plan_intent);
  if (trackingParams.billing_interval) redirectUrl.searchParams.set('billing_interval', trackingParams.billing_interval);
  if (trackingParams.landing_locale) redirectUrl.searchParams.set('landing_locale', trackingParams.landing_locale);
  if (trackingParams.source_intent) redirectUrl.searchParams.set('source_intent', trackingParams.source_intent);
  if (trackingParams.intent) redirectUrl.searchParams.set('intent', trackingParams.intent);
  if (trackingParams.source) redirectUrl.searchParams.set('source', trackingParams.source);
  const attributedUrl = window.instantTranscriberAcquisition?.decorateUrl(redirectUrl) || redirectUrl;
  return appendRedditAttributionParams(
    appendPromoteKitAttributionParams(
      appendMetaAttributionParams(attributedUrl),
    ),
  ).toString();
};

export default function GoogleOAuthButton({
  label,
  className = "",
  eventName = "landing_google_signup_click",
  showMicrocopy = false,
  microcopy,
  large = false,
  showOneClick = false,
  locale = DEFAULT_LOCALE,
  redirectPath = "/app/dashboard?from=landing",
  trackingParams: extraTrackingParams = {},
}) {
  const activeLocale = normalizeLocale(locale);
  const copy = getSiteCopy(activeLocale).cta;
  const buttonLabel = label || copy.google;
  const helperText = microcopy || copy.microcopy;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const pending = useRef(false);

  const handleGoogleSignup = async (event) => {
    if (pending.current) return;
    pending.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const clickLocation = getClickLocation(event.currentTarget);
      await afterNextPaint();
      const ctaLocation = extraTrackingParams.cta_location || clickLocation;
      const trackingParams = {
        auth_target: 'signup',
        auth_method: 'google',
        click_location: ctaLocation,
        cta_location: ctaLocation,
        landing_path: window.location.pathname,
        landing_locale: activeLocale,
        ...extraTrackingParams,
      };

      dispatchTrack('landing_cta_click', trackingParams);
      dispatchTrack(eventName, trackingParams);
      dispatchTrack('landing_auth_click', trackingParams);
      if (eventName !== 'landing_google_signup_click') {
        dispatchTrack('landing_google_signup_click', trackingParams);
      }

      await window.instantTranscriberAcquisition?.resolveGoogleIds();
      window.location.href = buildAppSignupUrl(redirectPath, trackingParams);
    } catch (error) {
      console.error("Google signup error:", error);
      setError(error.message || copy.googleError);
    } finally {
      pending.current = false;
      setIsLoading(false);
    }
  };

  return (
    <div className={`google-oauth-container ${className}`}>
      <button
        className={`google-oauth-btn ${large ? 'large' : ''}`}
        onClick={handleGoogleSignup}
        disabled={isLoading}
        aria-label={buttonLabel}
        data-umami-event={eventName}
      >
        <span className="google-icon-wrap"><GoogleIcon /></span>
        <span className="google-label">
          {isLoading ? copy.signingIn : buttonLabel}
          {showOneClick && <span className="one-click-pill">{oneClickLabels[activeLocale]}</span>}
        </span>
      </button>

      {showMicrocopy && (
        <p className="oauth-microcopy" aria-live="polite">
          {helperText}
        </p>
      )}

      {error && <div className="error-message">{error}</div>}


      <style jsx>{`
        .google-oauth-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .google-oauth-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          height: 52px;
          padding: 0 22px;
          background-color: #ffffff;
          border: 2px solid #dadce0;
          border-radius: 9999px; /* pill */
          font-size: 1.05rem;
          font-weight: 700;
          color: #1f2937;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 320px;
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
        }

        /* Larger, more prominent CTA (desktop) */
        .google-oauth-btn.large {
          height: 64px;
          min-width: 480px;
          font-size: 1.125rem;
        }
        .google-oauth-btn.large .google-icon-wrap svg { width: 20px; height: 20px; }
        @media (max-width: 640px) {
          .google-oauth-btn.large {
            width: 100%;
            min-width: 100%;
          }
        }

        .google-oauth-btn:hover:not(:disabled) {
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
          transform: translateY(-1px);
          border-color: #c1c7cd;
        }

        .google-oauth-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
        }

        .google-oauth-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .google-icon-wrap { display:flex; flex: 0 0 auto; }
        .google-label {
          display:flex;
          align-items:center;
          justify-content:center;
          gap:.5rem;
          min-width: 0;
        }
        .one-click-pill {
          font-size: .75rem;
          font-weight: 700;
          padding: .18rem .5rem;
          border: 1px solid #e5e7eb;
          border-radius: 9999px;
          background: #f9fafb;
        }

        @media (max-width: 420px) {
          .google-oauth-btn.large {
            padding: 0 14px;
            font-size: 1rem;
          }

          .one-click-pill {
            display: none;
          }
        }

        .error-message {
          color: #d93025;
          font-size: 0.9rem;
          text-align: center;
          max-width: 280px;
        }

        .oauth-microcopy {
          font-size: 0.9rem;
          color: #6b7280;
        }

      `}</style>
    </div>
  );
}
